import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Inbox, TriangleAlert, X } from "lucide-react";

import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import { getLoggedInUserDataFromLocalStorage } from "../../config/config";
import { selectLoginTokenInfo } from "../../redux/features/loginRegisterSlice";
import {
  fetchNotifications,
  selectNotificationsDatatWhole,
} from "../../redux/features/notificationsSlice";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { cn } from "../../lib/utils";
import NotificationItem from "./NotificationItem";

/**
 * Three skeleton rows shaped like real ones, rather than a spinner. A spinner
 * in the middle of a panel tells the user to wait; a skeleton tells them what
 * is arriving and stops the layout jumping when it does.
 */
const LoadingState = () => (
  <div className="flex flex-col">
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex gap-3 px-4 py-3.5">
        <Skeleton className="size-9 shrink-0 rounded-full" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-2/5" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-3/5" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Empty state that teaches the surface: it names what will show up here, so a
 * first-time user learns the feature instead of reading "no notifications".
 */
const EmptyState = ({ translate }) => (
  <div className="flex flex-col items-center px-8 py-14 text-center">
    <span className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
      <Inbox className="size-6" aria-hidden="true" />
    </span>
    <p className="mt-4 text-sm font-semibold text-foreground">
      {translate("You're all caught up", "لا توجد إشعارات جديدة")}
    </p>
    <p className="mt-1 max-w-[38ch] text-[13px] leading-5 text-muted-foreground">
      {translate(
        "Policy renewals, claim updates and announcements will appear here.",
        "ستظهر هنا تجديدات الوثائق وتحديثات المطالبات والإعلانات."
      )}
    </p>
  </div>
);

const ErrorState = ({ translate, onRetry }) => (
  <div className="flex flex-col items-center px-8 py-14 text-center">
    <span className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
      <TriangleAlert className="size-6" aria-hidden="true" />
    </span>
    <p className="mt-4 text-sm font-semibold text-foreground">
      {translate("Couldn't load notifications", "تعذر تحميل الإشعارات")}
    </p>
    <p className="mt-1 max-w-[38ch] text-[13px] leading-5 text-muted-foreground">
      {translate(
        "Check your connection and try again.",
        "تحقق من اتصالك وحاول مرة أخرى."
      )}
    </p>
    <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
      {translate("Try again", "إعادة المحاولة")}
    </Button>
  </div>
);

/**
 * Inbox contents, shared by both shells — the desktop popover and the mobile
 * sheet render exactly this, so the two never drift apart.
 *
 * `onClose` is only passed by the mobile sheet, which needs its own dismiss
 * affordance; the popover dismisses on outside-click and Esc via Radix.
 *
 * `TitleComponent` lets the sheet pass Radix's SheetTitle so the visible
 * heading *is* the dialog's accessible name. Rendering a separate sr-only title
 * would announce "Notifications" twice.
 */
const NotificationsPanel = ({ onClose, titleId, TitleComponent = "h2" }) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const { items, isLoading, error } = useSelector(selectNotificationsDatatWhole);

  // Redux hydrates from localStorage lazily, so a fresh load can have a valid
  // session the store has not caught up with yet.
  const accessToken =
    loginTokenInfo?.access_token ||
    getLoggedInUserDataFromLocalStorage()?.access_token;

  useEffect(() => {
    if (!accessToken) return;
    dispatch(fetchNotifications());
  }, [dispatch, accessToken]);

  const showList = !isLoading && items.length > 0;
  const showEmpty = !isLoading && !error && items.length === 0;
  const showError = !isLoading && error && items.length === 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex items-center gap-2 px-4 pb-3 pt-4">
        <TitleComponent
          id={titleId}
          className="text-base font-semibold tracking-[-0.01em] text-foreground"
        >
          {translate("Notifications", "الإشعارات")}
        </TitleComponent>

        <div className="ms-auto flex items-center gap-1">
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="size-8 text-muted-foreground hover:text-foreground"
              onClick={onClose}
              aria-label={translate("Close notifications", "إغلاق الإشعارات")}
            >
              <X aria-hidden="true" />
            </Button>
          )}
        </div>
      </header>

      <Separator />

      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-contain",
          // Momentum scrolling inside the mobile sheet.
          "[-webkit-overflow-scrolling:touch]"
        )}
      >
        {isLoading && <LoadingState />}
        {showError && (
          <ErrorState
            translate={translate}
            onRetry={() => dispatch(fetchNotifications())}
          />
        )}
        {showEmpty && <EmptyState translate={translate} />}
        {showList && (
          <ul className="flex flex-col divide-y divide-border">
            {items.map((notification) => (
              <NotificationItem
                key={notification?.NotificationId}
                notification={notification}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
